import { Injectable } from '@angular/core';
import { BaseAPI } from '@core/services/base-api.service';
import { Mapper } from '@core/services/base-mapper.service';
import { APIConstants } from '@shared/constants';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Customer } from '@features/customers/models/customer.model';
import { CustomerDetails } from '@features/customers/models/customer-details.model';
import { CustomerPaginationDetails } from '@features/customers/models/customer-pagination-details.model';
import {
  SortDirection,
  SortField,
  DateRangeType,
  LocationType,
  AddLocationType,
  Status,
} from '@shared-features/enums/shared-enums.enum';
import { filterNullEntity } from '@shared/utils';
import { CustomerEtaConfiguration } from '@shared-features/enums/shared-enums.enum';
import { Theme } from '@shared/models/theme';
import { CustomerWithLocation } from '@features/customers/models/customer-with-location.model';
import { MyCustomersList } from '@features/customers/models/my-customers-list.model';
import { LocationModel } from '@features/customers/models/location.model';
import { WalletDetails } from '../models/wallet-details.model';
import { WalletTransactionResponse } from '../models/wallet-transaction-response.model';
import { ExportedFile } from '@shared-features/models';
import { CustomerTransactions } from '@features/transactions/models/customer-transactions';
import { LocationStatus } from '../../shared-features/enums/location-status.enum';
import { HttpParams } from '@angular/common/http';
import { UserInvoiceType } from '@shared-features/enums/user-invoice-type.enum';
import { PricingMethod } from '@shared-features/enums/pricing-method';
import { CustomerInvoicesResponse } from '../models/customer-invoices-response.model';
import { CustomerInvoicesPayload } from '../models/customer-invoices.payload';
@Injectable({
  providedIn: 'root',
})
export class CustomerAPI {
  constructor(private baseAPI: BaseAPI, private mapper: Mapper) {}

  getAllCustomersWithPagination(
    offset?: number,
    limit?: number,
    status: Status[] = [],
    searchTerm?: string,
    dateRangeType: DateRangeType = DateRangeType.ALL_TIME,
    startFrom?: string,
    endAt?: string,
    sortDirection: SortDirection = SortDirection.None,
    sortField: SortField = SortField.None,
    crNumber?: number,
    title?: string
  ): Observable<CustomerPaginationDetails> {
    const params = filterNullEntity({
      offset,
      limit,
      sortDirection,
      sortField,
      dateRangeType,
      searchTerm,
      startFrom,
      endAt,
      crNumber,
      title,
    });

    for (let i = 0; i < status?.length; i++) {
      params[`status[${i}]`] = status[i];
    }
    return this.baseAPI
      .get(APIConstants.API_GET_ALL_CUSTOMERS_WITH_PAGINATION, {
        params: filterNullEntity<typeof params>(params),
      })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(CustomerPaginationDetails, res.data);
        })
      );
  }

  addCustomer(customer: Customer, commercialRegistrationId: number = null): Observable<Customer> {
    const body = filterNullEntity({
      title: customer.title,
      contactFirstName: customer.contactFirstName,
      contactLastName: customer.contactLastName,
      customerCommercialNameAr: customer.customerCommercialNameAr,
      customerCommercialNameEn: customer.customerCommercialNameEn,
      address: customer.address,
      addressAr: customer.addressAr,
      email: customer.email,
      mobileNumber: customer.mobileNumber,
      crNumber: customer.crNumber,
      commercialRegistrationId,
      dueDays: customer.dueDays,
      waitingChargePerDay: customer.waitingChargePerDay,
      localVatPercentage: customer.localVatPercentage || 0,
      exportVatPercentage: customer.exportVatPercentage || 0,
      sisterCompanyVatPercentage: customer.sisterCompanyVatPercentage || 0,
      sisterCompanies: customer.sisterCompanies?.map((receiver) => receiver.id),
      customerType: customer.customerType,
      isFromOnlineBooking: customer.isFromOnlineBooking,
      reference: customer?.reference,
      pricingMethod: PricingMethod.PerTradeLane,
      customerInvoiceType: UserInvoiceType.Both,
    });
    return this.baseAPI.post(APIConstants.API_ADD_CUSTOMER, body).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  getCustomerById(customerId: string): Observable<CustomerDetails> {
    return this.baseAPI.get(APIConstants.API_GET_CUSTOMER_BY_ID + customerId).pipe(
      map((res) => {
        return this.mapper.fromJson(CustomerDetails, res.data);
      })
    );
  }

  updateCustomer(
    body: Partial<Customer>,
  ): Observable<Customer> {

    return this.baseAPI.put(APIConstants.API_UPDATE_CUSTOMER, filterNullEntity(body)).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  updateCustomerEta(body: {
    customerId: number;
    etaConfiguration: CustomerEtaConfiguration;
  }): Observable<Customer> {
    return this.baseAPI.put(APIConstants.API_UPDATE_CUSTOMER_ETA, filterNullEntity(body)).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  setTheme({ id, themeName, color, mediaId }: Theme): Observable<Theme> {
    const endpoint = id
      ? APIConstants.API_UPDATE_CUSTOMER_THEME
      : APIConstants.API_ADD_CUSTOMER_THEME;
    const updatedTheme: Partial<Theme> = { themeName, color, mediaId };
    if (id) {
      updatedTheme.id = id;
    }
    return this.baseAPI
      .post(endpoint, updatedTheme)
      .pipe(map((res) => this.mapper.fromJson(Theme, res.data)));
  }

  addLocationCustomer(item: CustomerWithLocation, commercialRegistrationId?: number): Observable<CustomerWithLocation> {
    const body = {
      customer: {
        title: item.customer.title,
        contactFirstName: item.customer.contactFirstName,
        contactLastName: item.customer.contactLastName,
        customerCommercialNameAr: item.customer.customerCommercialNameAr,
        customerCommercialNameEn: item.customer.customerCommercialNameEn,
        address: item.customer.address,
        addressAr: item.customer.addressAr,
        email: item.customer.email,
        mobileNumber: item.customer.mobileNumber,
        crNumber: item.customer.crNumber,
        commercialRegistrationId: item.customer?.commercialRegistrationId || commercialRegistrationId || null,
        dueDays: item.customer.dueDays,
        waitingChargePerDay: item.customer.waitingChargePerDay,
        localVatPercentage: item.customer.localVatPercentage || 0,
        exportVatPercentage: item.customer.exportVatPercentage || 0,
        sisterCompanyVatPercentage: item.customer.sisterCompanyVatPercentage || 0,
        sisterCompanies: item.customer.sisterCompanies.map((receiver) => receiver.id) ?? [],
        customerType: item.customer.customerType,
        isFromOnlineBooking: item.customer.isFromOnlineBooking,
        reference: item.customer?.reference,
        pricingMethod: PricingMethod.PerTradeLane,
        customerInvoiceType: UserInvoiceType.Both,
      },
      ...(item?.location && {
        location: {
          ...item.location,
          type: LocationType.Customer,
          dropDownDisplayName: null,
          dropDownValue: null,
          latlng: null,
        }
      })
    }

    return this.baseAPI.post(APIConstants.ADD_LOCATION_CUSTOMER, filterNullEntity(body)).pipe(
      map((res) => {
        return this.mapper.fromJson(CustomerWithLocation, res.data);
      })
    );
  }

  getMyCustomers(payload: {
    customerId: number;
    searchTerm?: string;
    offset?: number;
    limit?: number;
  }): Observable<MyCustomersList> {
    return this.baseAPI
      .get(APIConstants.GET_MY_CUSTOMERS, { params: filterNullEntity(payload) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(MyCustomersList, res.data);
        })
      );
  }

  updateRCustomers(body: { rCustomerId: number; ToBeDeleted: boolean }): Observable<string> {
    return this.baseAPI.post(APIConstants.UPDATE_R_CUSTOMERS, filterNullEntity(body));
  }

  getCustomerLocations(
    customerId: number,
    locationStatus?: LocationStatus
  ): Observable<LocationModel[]> {
    let params = new HttpParams();
    if (locationStatus) {
      params = params.set('locationStatus', locationStatus);
    }
    return this.baseAPI.get(`${APIConstants.CUSTOMER_LOCATIONS}/${customerId}`, { params }).pipe(
      map((res) => {
        const customerLocations = this.mapper.fromJson(
          LocationModel,
          (res.data?.customer?.locations || []).map((item) => ({
            ...item,
            locationType: AddLocationType.Location,
          }))
        );

        const childCustomers = this.mapper.fromJson(
          LocationModel,
          (res.data?.customer?.childrenLocations || []).map((item) => ({
            ...item,
            locationType: AddLocationType.Customer,
          }))
        );
        return [...customerLocations, ...childCustomers];
      })
    );
  }
  getCustomerTemplate(customerId: number): Observable<Customer> {
    return this.baseAPI.get(`${APIConstants.GET_CREATION_TEMPLATE}/${customerId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  getWalletTransactions(payload: {
    customerId: number;
    offset?: number;
    limit?: number;
    searchTerm?: string;
    dateRangeType?: DateRangeType;
    startFrom?: string;
    endAt?: string;
    sortField?: SortField;
    sortDirection?: SortDirection;
  }): Observable<WalletTransactionResponse> {
    return this.baseAPI
      .get(APIConstants.GET_WALLET_TRANSACTIONS, { params: filterNullEntity(payload) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(WalletTransactionResponse, res.data);
        })
      );
  }

  exportAllTransactions(payload: {
    customerId: number;
    offset?: number;
    limit?: number;
    searchTerm?: string;
    dateRangeType?: DateRangeType;
    startFrom?: string;
    endAt?: string;
    isForExport?: boolean;
    sortField?: SortField;
    sortDirection?: SortDirection;
  }): Observable<ExportedFile> {
    return this.baseAPI
      .get(APIConstants.GET_WALLET_TRANSACTIONS, { params: filterNullEntity(payload) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(ExportedFile, res.data.transactions);
        })
      );
  }

  getWalletSummary(customerId: number): Observable<WalletDetails> {
    return this.baseAPI.get(APIConstants.GET_WALLET_SUMMARY + `/${customerId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(WalletDetails, res.data);
      })
    );
  }

  createVirtualIban(customerId: number): Observable<string> {
    return this.baseAPI
      .post(APIConstants.CREATE_VIRTUAL_IBAN, filterNullEntity({ customerId }))
      .pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  getVirtualIban(customerId: number): Observable<string> {
    return this.baseAPI.get(`${APIConstants.GET_VIRTUAL_IBAN}/${customerId}`).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  getCustomerTransactions(customerId: number): Observable<[CustomerTransactions]> {
    return this.baseAPI.get(`${APIConstants.GET_CUSTOMER_TRANSACTIONS}/${customerId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(CustomerTransactions, res.data);
      })
    );
  }

  getInvoicesByStatus(payload: CustomerInvoicesPayload): Observable<CustomerInvoicesResponse> {
    return this.baseAPI.get(APIConstants.GET_INVOICES_BY_STATUS, { params: filterNullEntity(payload) });
  }
}
